import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Alert,
  Pagination,
  CircularProgress,
} from '@mui/material';
import {
  History as HistoryIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Email as EmailIcon,
  Reply as ReplyIcon,
  TrendingUp as TrendingUpIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useScoutMessage } from '../../hooks/useScoutMessage';
import { useCompanyId } from '../../hooks/useCompanyId';
import { CompanyLayout } from '../../components/Layout';
import { ScoutMessageDetailModal } from '../../components/Scout/ScoutMessageDetailModal';
import {
  ScoutMessageStatus,
  ScoutMessageResult,
} from '../../types/scout-message';

const ScoutHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { companyId } = useCompanyId();
  const {
    isLoading,
    error,
    history,
    historyStats,
    historyPagination,
    loadHistory,
  } = useScoutMessage();

  const [currentPage, setCurrentPage] = useState(1);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(
    null
  );

  useEffect(() => {
    loadHistory(currentPage);
  }, [loadHistory, currentPage]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleViewDetail = (messageId: number) => {
    setSelectedMessageId(messageId);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedMessageId(null);
  };

  const getStatusColor = (status: ScoutMessageStatus) => {
    switch (status) {
      case ScoutMessageStatus.SENT:
        return 'info';
      case ScoutMessageStatus.DELIVERED:
        return 'primary';
      case ScoutMessageStatus.READ:
        return 'warning';
      case ScoutMessageStatus.REPLIED:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: ScoutMessageStatus) => {
    switch (status) {
      case ScoutMessageStatus.SENT:
        return '送信済み';
      case ScoutMessageStatus.DELIVERED:
        return '配信済み';
      case ScoutMessageStatus.READ:
        return '開封済み';
      case ScoutMessageStatus.REPLIED:
        return '返信あり';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: ScoutMessageStatus) => {
    switch (status) {
      case ScoutMessageStatus.SENT:
        return <SendIcon fontSize="small" />;
      case ScoutMessageStatus.DELIVERED:
        return <EmailIcon fontSize="small" />;
      case ScoutMessageStatus.READ:
        return <VisibilityIcon fontSize="small" />;
      case ScoutMessageStatus.REPLIED:
        return <ReplyIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading && !history.length) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <CompanyLayout>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'flex-start', md: 'space-between' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 2, md: 0 },
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 1,
                color: '#111827',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <HistoryIcon sx={{ mr: 1 }} />
              スカウト送信履歴
            </Typography>
            <Typography variant="body2" color="text.secondary">
              送信したスカウトメッセージの一覧と統計を確認できます
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              alignSelf: { xs: 'flex-end', md: 'center' },
            }}
          >
            <Tooltip title="履歴を更新">
              <IconButton
                onClick={() => loadHistory(currentPage)}
                disabled={isLoading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/company/scout/compose')}
              size="small"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
                whiteSpace: 'nowrap',
              }}
            >
              新規スカウト作成
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 統計情報 */}
        {historyStats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <SendIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" color="primary">
                    {historyStats.totalSent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    総送信数
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <VisibilityIcon
                    color="warning"
                    sx={{ fontSize: 40, mb: 1 }}
                  />
                  <Typography variant="h4" color="warning.main">
                    {historyStats?.readRate?.toFixed(1) || '0.0'}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    開封率
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ReplyIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" color="success.main">
                    {historyStats?.replyRate?.toFixed(1) || '0.0'}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    返信率
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUpIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" color="info.main">
                    {historyStats.quotaUsage?.today?.used || 0}/
                    {historyStats.quotaUsage?.today?.limit || 50}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    今日の送信数
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* スカウト履歴テーブル */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>送信日時</TableCell>
                  <TableCell>学生名</TableCell>
                  <TableCell>件名</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>開封日時</TableCell>
                  <TableCell>返信日時</TableCell>
                  <TableCell align="center">アクション</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((message: ScoutMessageResult) => (
                  <TableRow key={message.id} hover>
                    <TableCell>{formatDate(message.sentAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {message.recipientName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {message.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(message.status) || undefined}
                        label={getStatusLabel(message.status)}
                        color={getStatusColor(message.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {message.readAt ? (
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(message.readAt)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          未開封
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {message.repliedAt ? (
                        <Typography variant="body2" color="success.main">
                          {formatDate(message.repliedAt)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          未返信
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="詳細を表示">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetail(message.id)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Empty State */}
          {!isLoading && history.length === 0 && (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <HistoryIcon
                sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                スカウト履歴がありません
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                最初のスカウトメッセージを送信してみましょう
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/scouts/compose')}
              >
                スカウト作成
              </Button>
            </Box>
          )}

          {/* Pagination */}
          {historyPagination && historyPagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={historyPagination.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                disabled={isLoading}
              />
            </Box>
          )}
        </Paper>

        {/* スカウトメッセージ詳細モーダル */}
        <ScoutMessageDetailModal
          open={detailModalOpen}
          messageId={selectedMessageId}
          companyId={companyId}
          onClose={handleCloseDetailModal}
        />
      </Container>
    </CompanyLayout>
  );
};

export default ScoutHistoryPage;
